import { z } from 'zod';
import { normalizedUuidSchema as uuidSchema } from './common';
import { designBodySchema } from './design';
import { guestContactMethodSchema } from './guest-contact';
import { memberPaymentSchema } from './payment';
import {
  quoteCalendarDateSchema,
  quoteIntegerSchema,
  quoteOptionalTextSchema,
  quoteQuantitySchema,
  quoteRequiredTextSchema,
  quoteRowSchema,
  quoteWarningsSchema,
} from './quotes';
import {
  MAX_DESTINATIONS,
  QUOTE_LINE_KINDS,
  quoteDestinationSchema,
  SHIPPING_FEE_MAX,
  shippingRegionSchema,
} from './shipping';

export const SHIPPING_LINES_INCOMPLETE_MESSAGE = '배송지마다 배송비를 넣어 주세요.';
/**
 * 견적 줄(spec D6) — `SHIPPING` 은 배송지마다 하나(수량 1, 단가 ≥ 0)이고 `destinationId` 가 있다. 짝·중복·누락 규칙은 서버가 본다
 * (refine 을 두면 extend 할 수 없다). 종류를 빼고 보내면 상품 줄이다 — 배송비 줄을 모르는 편집기 입력과 호환.
 */
export const quoteDraftLineSchema = z.strictObject({
  name: quoteRequiredTextSchema.max(100),
  quantity: quoteQuantitySchema,
  unitPrice: quoteIntegerSchema,
  sortOrder: quoteIntegerSchema.min(0),
  kind: z.enum(QUOTE_LINE_KINDS).default('PRODUCT'),
  destinationId: uuidSchema.nullable().default(null),
});
export type QuoteDraftLine = z.infer<typeof quoteDraftLineSchema>;
/** 초안 편집 칸 — 임시 저장 입력과 관리자 초안 DTO 가 공유한다. */
const quoteDraftContentSchema = z.strictObject({
  quoteRequestId: uuidSchema,
  versionId: uuidSchema,
  lines: z.array(quoteDraftLineSchema).readonly(),
  productionStartNote: quoteOptionalTextSchema(),
  deliveryNote: quoteOptionalTextSchema(),
  deliveryMethodNote: quoteOptionalTextSchema(),
  validUntil: quoteCalendarDateSchema.nullable(),
  specialTerms: quoteOptionalTextSchema(),
});
/**
 * 초안 낙관적 동시성 토큰 — 편집기가 불러왔거나 마지막으로 저장한 초안의 `updatedAt`(ISO). 서버는 견적 행 잠금 아래
 * 현재 초안의 `updated_at` 과 비교해 다르면(다른 운영자가 먼저 저장) `QUOTE_VERSION_OUTDATED`(CONFLICT)로 거절한다.
 */
export const quoteDraftTokenSchema = z.iso.datetime();
export const quoteDraftInputSchema = quoteDraftContentSchema.extend({
  expectedDraftUpdatedAt: quoteDraftTokenSchema,
});
export type QuoteDraftInput = z.infer<typeof quoteDraftInputSchema>;
/** 발행 입력 — 서버는 저장된 초안을 발행하므로 화면이 본 초안(토큰)과 같을 때만 발행한다. */
export const issueQuoteInputSchema = z.strictObject({
  quoteRequestId: uuidSchema,
  versionId: uuidSchema,
  expectedDraftUpdatedAt: quoteDraftTokenSchema,
});
export type IssueQuoteInput = z.infer<typeof issueQuoteInputSchema>;

/** 실제 사업자 정보는 승인된 설정 공급자가 주입한다. 임의 기본값을 두지 않는다. */
export const quoteIssuerSnapshotSchema = z.strictObject({
  companyName: quoteRequiredTextSchema,
  businessRegistrationNumber: quoteRequiredTextSchema,
  representativeName: quoteRequiredTextSchema,
  address: quoteRequiredTextSchema,
  contact: quoteRequiredTextSchema,
});
export type QuoteIssuerSnapshot = z.infer<typeof quoteIssuerSnapshotSchema>;
/** 공통 문구와 별첨은 각각 원문과 버전을 주입한다. */
export const quoteTermsSnapshotSchema = z.strictObject({
  termsText: quoteRequiredTextSchema,
  termsVersion: quoteRequiredTextSchema,
  withdrawalNoticeText: quoteRequiredTextSchema,
  withdrawalNoticeVersion: quoteRequiredTextSchema,
});
export type QuoteTermsSnapshot = z.infer<typeof quoteTermsSnapshotSchema>;
export const quoteVersionLineSchema = quoteDraftLineSchema.extend({
  id: uuidSchema,
  amount: quoteIntegerSchema,
});
export type QuoteVersionLine = z.infer<typeof quoteVersionLineSchema>;
/**
 * 관리자 초안의 배송지별 배송비 제안 — 지금 '배송비 설정' 기준(spec §5-2). 저장된 줄 단가와 별개라 설정을 바꿔도 초안·발행본은 그대로다(D9).
 * 지역을 알 수 없는 배송지(우편번호가 5자리 숫자가 아닌 이행 행)는 `region` 이 null 이고 기본(일반) 배송비를 제안한다.
 */
export const suggestedShippingFeeSchema = z.strictObject({
  destinationId: uuidSchema,
  region: shippingRegionSchema.nullable(),
  fee: quoteIntegerSchema.min(0).max(SHIPPING_FEE_MAX),
});
export type SuggestedShippingFee = z.infer<typeof suggestedShippingFeeSchema>;
/** 줄에서 계산한 표시용 금액(`splitQuoteAmounts`) — 상품 = PRODUCT 줄 합, 배송비 = SHIPPING 줄 합. */
const quoteAmountBreakdownShape = {
  productAmount: quoteIntegerSchema,
  shippingAmount: quoteIntegerSchema.min(0),
};

/** 발행본 DTO의 공급자 정보는 검증된 공개 필드만 포함한다. DB raw JSON을 내보내지 않는다. */
export const issuedQuoteVersionSchema = z.strictObject({
  id: uuidSchema,
  version: quoteIntegerSchema.min(1),
  issuedAt: z.iso.datetime(),
  lines: z.array(quoteVersionLineSchema).min(1),
  totalAmount: quoteIntegerSchema.min(1),
  supplyAmount: quoteIntegerSchema,
  vatAmount: quoteIntegerSchema,
  ...quoteAmountBreakdownShape,
  productionStartNote: quoteRequiredTextSchema,
  deliveryNote: quoteRequiredTextSchema,
  deliveryMethodNote: quoteRequiredTextSchema,
  validUntil: quoteCalendarDateSchema,
  specialTerms: z.string().nullable(),
  issuer: quoteIssuerSnapshotSchema,
  recipientCompanyName: z.string().nullable(),
  recipientContactName: quoteRequiredTextSchema,
  termsText: quoteRequiredTextSchema,
  termsVersion: quoteRequiredTextSchema,
});
export type IssuedQuoteVersion = z.infer<typeof issuedQuoteVersionSchema>;
export const adminQuoteDraftSchema = quoteDraftContentSchema.extend({
  version: quoteIntegerSchema.min(1),
  totalAmount: quoteIntegerSchema,
  supplyAmount: quoteIntegerSchema,
  vatAmount: quoteIntegerSchema,
  ...quoteAmountBreakdownShape,
  /** 배송지마다(sortOrder 순) 지금 설정 기준 배송비 제안 — 편집 화면이 배송비 줄을 채울 때 쓴다. */
  suggestedShippingFees: z.array(suggestedShippingFeeSchema).max(MAX_DESTINATIONS),
  /** 낙관적 동시성 토큰(`quoteDraftTokenSchema`) — 저장·발행 입력의 `expectedDraftUpdatedAt` 으로 돌려보낸다. */
  updatedAt: quoteDraftTokenSchema,
});
export type AdminQuoteDraft = z.infer<typeof adminQuoteDraftSchema>;
/** 디자인·인쇄 방식 칸은 일반 견적(편집영역 없는 상품, 일반 견적 설계 §5-3)이면 모두 null 이다. */
export const quoteDesignSnapshotSchema = z.strictObject({
  designId: uuidSchema.nullable(),
  designName: z.string().nullable(),
  designSchemaVersion: quoteIntegerSchema.min(1).nullable(),
  designBody: designBodySchema.nullable(),
  productId: uuidSchema,
  colorOptionId: uuidSchema,
  colorName: z.string(),
  printMethodId: uuidSchema.nullable(),
  printMethodName: z.string().nullable(),
  editAreaId: uuidSchema.nullable(),
  /** 견적 사본 썸네일 표시 주소 — worker 견적 렌더가 `thumbnail_key` 를 연결하기 전에는 null. */
  thumbnailUrl: z.string().nullable(),
});
export type QuoteDesignSnapshot = z.infer<typeof quoteDesignSnapshotSchema>;

/** 목록 행 칸을 물려받되 목록 전용 썸네일 칸(`thumbnailUrl`)은 뺀다 — 상세의 그림은 `design.thumbnailUrl` 하나다(설계 D9). */
export const customerQuoteDetailSchema = quoteRowSchema.omit({ thumbnailUrl: true }).extend({
  desiredDate: quoteCalendarDateSchema.nullable(),
  memo: z.string().nullable(),
  companyName: z.string().nullable(),
  contactName: z.string(),
  /** 요청 배송지(sortOrder 순, spec §6-1). 배송지 없이 접수된 견적은 빈 배열이다. */
  destinations: z.array(quoteDestinationSchema).max(MAX_DESTINATIONS),
  design: quoteDesignSnapshotSchema,
  latestIssuedVersion: issuedQuoteVersionSchema.nullable(),
  rejectedReason: z.string().nullable(),
  /**
   * 살아 있는 주문의 결제 요약 — 주문 상세와 같은 선택 규칙(승인 → CANCELED 최신 → 그 밖 최신, R16)으로 고른 결제 하나.
   * 주문 id·주문 고객 화면 이름(`orderId`·`orderDisplayStatus`)은 목록 행(`quoteRowSchema`)의 칸을 그대로 상속한다.
   */
  payment: memberPaymentSchema.nullable(),
});
export type CustomerQuoteDetail = z.infer<typeof customerQuoteDetailSchema>;
export const quoteNoteSchema = z.strictObject({
  id: uuidSchema,
  authorName: z.string(),
  body: z.string(),
  createdAt: z.iso.datetime(),
});
export const quoteRevisionRequestSchema = z.strictObject({
  id: uuidSchema,
  versionId: uuidSchema,
  reason: z.string(),
  createdAt: z.iso.datetime(),
});
/**
 * 관리자 상세의 비회원 요약(비회원 견적 설계 G14) — 연락 방법, 이메일 고객의 이메일(휴대폰은 '보기'로만 복호화하므로 싣지 않는다),
 * 회원 계정 연결 여부와 시각.
 */
export const adminQuoteGuestSchema = z.strictObject({
  contactMethod: guestContactMethodSchema,
  email: z.string().nullable(),
  claimed: z.boolean(),
  claimedAt: z.iso.datetime().nullable(),
});
export type AdminQuoteGuest = z.infer<typeof adminQuoteGuestSchema>;
export const adminQuoteDetailSchema = customerQuoteDetailSchema.extend({
  /** 견적 주인 회원의 이메일. 아직 회원 계정에 연결되지 않은 비회원 견적은 null. */
  userEmail: z.string().nullable(),
  /** 회원이 요청한 견적은 null. */
  guest: adminQuoteGuestSchema.nullable(),
  /** 고객 거절 사유(spec §7-7, 선택) — 운영자만 본다. */
  declinedReason: z.string().nullable(),
  warnings: quoteWarningsSchema,
  draft: adminQuoteDraftSchema.nullable(),
  notes: z.array(quoteNoteSchema),
  revisionRequests: z.array(quoteRevisionRequestSchema),
  issuedVersions: z.array(issuedQuoteVersionSchema),
  assetIds: z.array(uuidSchema),
});
export type AdminQuoteDetail = z.infer<typeof adminQuoteDetailSchema>;
